export type MessageEventTypeOption = { value: string; label: string };

// The event-type vocabularies the message row's metric select offers, in
// wire-facing values: `*_email` for email, `*_action` for other actions,
// `*_content` for anonymous in-app broadcasts. Labels read as a sentence with
// the message as the subject ("any push has been sent").

const EMAIL_FILTER_LABELS: [string, string][] = [
  ['sent', 'been sent'],
  ['delivered', 'been delivered'],
  ['opened', 'been opened'],
  ['clicked', 'been clicked'],
  ['dropped', 'been suppressed'],
  ['converted', 'converted'],
  ['bounced', 'bounced'],
  ['failed', 'failed to deliver'],
  ['spammed', 'been marked as spam'],
  ['unsubscribed', 'caused unsubscribe'],
];

const ACTION_FILTER_LABELS: [string, string][] = [
  ['sent', 'been sent'],
  ['delivered', 'been delivered'],
  ['opened', 'been opened'],
  ['clicked', 'been clicked'],
  ['converted', 'converted'],
  ['bounced', 'bounced'],
  ['failed', 'failed to send'],
  ['dropped', 'been suppressed'],
  ['undeliverable', 'been marked as undeliverable'],
  ['spammed', 'been marked as spam'],
  ['replied', 'been replied to'],
];

const CONTENT_FILTER_LABELS: [string, string][] = [
  ['viewed', 'been viewed'],
  ['clicked', 'been clicked'],
  ['converted', 'converted'],
];

const SUBJECT_EMAIL_FILTERS: MessageEventTypeOption[] = EMAIL_FILTER_LABELS.map(([value, label]) => ({
  value: `${value}_email`,
  label,
}));

const SUBJECT_ACTION_FILTERS: MessageEventTypeOption[] = ACTION_FILTER_LABELS.map(([value, label]) => ({
  value: `${value}_action`,
  label,
}));

const SUBJECT_CONTENT_FILTERS: MessageEventTypeOption[] = CONTENT_FILTER_LABELS.map(([value, label]) => ({
  value: `${value}_content`,
  label,
}));

/**
 * The metrics each action channel records, from the action catalog's
 * `segmentFilters` (services indexes exactly these per channel). Email is not
 * here — its vocabulary is the whole email list above.
 */
const SEGMENT_FILTERS_BY_CHANNEL = new Map<string, readonly string[]>([
  ['push', ['sent', 'failed', 'converted', 'opened', 'bounced', 'dropped', 'undeliverable', 'delivered']],
  ['slack', ['sent', 'failed', 'clicked']],
  ['twilio', ['sent', 'failed', 'converted', 'bounced', 'clicked', 'delivered', 'replied', 'dropped']],
  ['sms', ['sent', 'failed', 'converted', 'bounced', 'clicked', 'delivered', 'replied', 'dropped']],
  ['urban_airship', ['sent', 'failed', 'converted', 'clicked']],
  // Webhook's catalog entry is `optionallyConverts`; the metric select asks
  // with conversion allowed, which appends `converted` to its recorded list.
  ['webhook', ['sent', 'failed', 'clicked', 'spammed', 'converted']],
  ['in_app', ['sent', 'failed', 'clicked', 'converted', 'opened']],
  ['inbox', ['sent', 'failed', 'clicked', 'converted', 'opened']],
  ['line', ['sent', 'failed', 'delivered', 'clicked', 'converted']],
  ['whatsapp', ['sent', 'failed', 'delivered', 'clicked', 'opened', 'converted']],
]);

const IN_APP_BROADCAST_SEGMENT_FILTERS = ['viewed', 'clicked', 'converted'];

function sortedByLabel(options: MessageEventTypeOption[]): MessageEventTypeOption[] {
  return [...options].sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
}

/**
 * The event types the metric select offers for a channel.
 *
 * Email keeps its list's own order, hiding "been opened" when the workspace
 * has open tracking off — unless the saved condition already uses it. Action
 * channels offer their recorded metrics, label-sorted; an anonymous in-app
 * broadcast value (`iac…`) swaps to the content vocabulary.
 */
export function eventTypeOptionsFor(
  channel: string,
  eventName: string,
  currentEventType: string,
  { emailOpenTrackingEnabled = true }: { emailOpenTrackingEnabled?: boolean } = {},
): MessageEventTypeOption[] {
  if (channel === 'email') {
    return SUBJECT_EMAIL_FILTERS.filter((filter) => {
      if (filter.value !== 'opened_email') {
        return true;
      }
      return emailOpenTrackingEnabled || currentEventType === 'opened_email';
    });
  }

  if (channel === 'in_app' && eventName.startsWith('iac')) {
    const contentEvents = IN_APP_BROADCAST_SEGMENT_FILTERS.map((filter) => `${filter}_content`);
    return sortedByLabel(SUBJECT_CONTENT_FILTERS.filter((filter) => contentEvents.includes(filter.value)));
  }

  const actionEvents = (SEGMENT_FILTERS_BY_CHANNEL.get(channel) ?? []).map((filter) => `${filter}_action`);
  return sortedByLabel(SUBJECT_ACTION_FILTERS.filter((filter) => actionEvents.includes(filter.value)));
}

const EVENT_TYPE_LABELS = new Map<string, string>(
  [...SUBJECT_EMAIL_FILTERS, ...SUBJECT_ACTION_FILTERS, ...SUBJECT_CONTENT_FILTERS].map((option) => [
    option.value,
    option.label,
  ]),
);

/**
 * The metric select must keep showing a saved event type the channel's
 * vocabulary does not offer (for example `clicked_action` on push), or the
 * select renders blank. Display-only: change handlers keep validating against
 * the raw {@link eventTypeOptionsFor} list, so channel and message switches
 * still snap to a valid metric.
 */
export function withCurrentEventTypeOption(
  options: MessageEventTypeOption[],
  currentEventType: string,
): MessageEventTypeOption[] {
  if (currentEventType === '' || options.some((option) => option.value === currentEventType)) {
    return options;
  }
  return [...options, { value: currentEventType, label: EVENT_TYPE_LABELS.get(currentEventType) ?? currentEventType }];
}

/**
 * What the metric select lands on after a channel change: the first option of
 * the new channel's list.
 */
export function defaultEventTypeFor(
  channel: string,
  eventName: string,
  options?: { emailOpenTrackingEnabled?: boolean },
): string {
  return eventTypeOptionsFor(channel, eventName, '', options)[0]?.value ?? 'sent_action';
}
